import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProjetos } from '../hooks/useProjetos';
import { useConvites } from '../hooks/useConvites';
import { cores } from '../styles/cores';
import { Btn, Campo, EstadoVazio, Input, Modal, Select, Spinner } from '../components/UI';

function BadgeConviteStatus({ status }) {
  const mapa = {
    pendente: { cor: cores.alerta, fundo: '#FCEFDB', rotulo: 'Aguardando' },
    aceito:   { cor: cores.sucesso, fundo: '#E1F5EE', rotulo: 'Aceito' },
    recusado: { cor: cores.erro,    fundo: '#FBE6E6', rotulo: 'Recusado' },
  };
  const e = mapa[status] || mapa.pendente;
  return (
    <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, color: e.cor, backgroundColor: e.fundo }}>
      {e.rotulo}
    </span>
  );
}

function BadgePermissao({ permissao }) {
  const bloqueado = permissao === 'somente_leitura';
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: 999,
        color: bloqueado ? cores.erro : cores.sucesso,
        backgroundColor: bloqueado ? '#FBE6E6' : '#E1F5EE',
      }}
    >
      {bloqueado ? '🔒 Somente leitura' : '✅ Acesso ativo'}
    </span>
  );
}

const COR_BORDA_STATUS = { aceito: cores.sucesso, recusado: cores.erro, pendente: cores.alerta };

export function Convites() {
  const { usuario } = useAuth();
  const { projetos } = useProjetos();
  const {
    convitesRecebidos,
    convitesEnviados,
    carregando,
    enviarConvite,
    aceitarConvite,
    recusarConvite,
    removerMembro,
    alterarPermissao,
  } = useConvites(usuario?.uid, usuario?.email);

  const [modalAberto, setModalAberto] = useState(false);
  const [form, setForm] = useState({ paraEmail: '', projetoId: '' });
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState('');

  const projetosDono = projetos.filter((p) => p.ownerUid === usuario?.uid);

  // Permissão atual de um membro num projeto
  const permissaoAtual = (projetoId, paraUid) => {
    const projeto = projetos.find((p) => p.id === projetoId);
    return projeto?.membrosPermissoes?.[paraUid] ?? 'ativo';
  };

  const aoEnviar = async (e) => {
    e.preventDefault();
    setErroEnvio('');
    if (form.paraEmail.toLowerCase().trim() === usuario?.email?.toLowerCase().trim()) {
      setErroEnvio('Você não pode convidar a si mesmo.');
      return;
    }
    const projeto = projetosDono.find((p) => p.id === form.projetoId);
    if (!projeto) { setErroEnvio('Selecione um projeto.'); return; }
    const jaExiste = convitesEnviados.some(
      (c) => c.projetoId === form.projetoId && c.paraEmail === form.paraEmail.toLowerCase().trim() && c.status !== 'recusado'
    );
    if (jaExiste) { setErroEnvio('Já existe um convite ativo para este usuário neste projeto.'); return; }

    setEnviando(true);
    await enviarConvite({ projetoId: projeto.id, projetoNome: projeto.nome, paraEmail: form.paraEmail, deNome: usuario.nome || usuario.email });
    setEnviando(false);
    setForm({ paraEmail: '', projetoId: '' });
    setModalAberto(false);
  };

  const aoRemoverMembro = async (convite) => {
    if (!window.confirm(`Remover "${convite.paraNome || convite.paraEmail}" do projeto "${convite.projetoNome}"?\n\nEle perderá o acesso ao projeto.`)) return;
    await removerMembro(convite.id, convite.projetoId, convite.paraUid);
  };

  const aoAlternarPermissao = async (convite) => {
    const atual = permissaoAtual(convite.projetoId, convite.paraUid);
    const nova = atual === 'somente_leitura' ? 'ativo' : 'somente_leitura';
    await alterarPermissao(convite.projetoId, convite.paraUid, nova);
  };

  if (carregando) return <Spinner />;

  const recebidosPendentes   = convitesRecebidos.filter((c) => c.status === 'pendente');
  const recebidosRespondidos = convitesRecebidos.filter((c) => c.status !== 'pendente');
  const enviadosAceitos      = convitesEnviados.filter((c) => c.status === 'aceito');
  const enviadosOutros       = convitesEnviados.filter((c) => c.status !== 'aceito');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: cores.texto }}>Convites</h1>
        {projetosDono.length > 0 && <Btn onClick={() => setModalAberto(true)}>+ Enviar convite</Btn>}
      </div>
      <p style={{ margin: '0 0 28px', fontSize: 14, color: cores.textoSecundario }}>
        Gerencie convites para participar de projetos
      </p>

      {/* -------- Recebidos pendentes -------- */}
      {recebidosPendentes.length > 0 && (
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 15, color: cores.texto, margin: '0 0 12px' }}>
            📬 Convites recebidos ({recebidosPendentes.length})
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {recebidosPendentes.map((c) => (
              <div key={c.id} style={cardStyle(cores.alerta)}>
                <span style={{ fontSize: 22 }}>🤝</span>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 14, color: cores.texto }}>
                    <strong>{c.deNome}</strong> te convidou para o projeto <strong>{c.projetoNome}</strong>
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 12, color: cores.textoSecundario }}>Você quer participar deste projeto?</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn variante="secundario" onClick={() => recusarConvite(c.id)}>Recusar</Btn>
                  <Btn onClick={() => aceitarConvite(c.id, c, usuario.nome || usuario.email, usuario.uid)}>Aceitar</Btn>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* -------- Histórico recebidos -------- */}
      {recebidosRespondidos.length > 0 && (
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 15, color: cores.texto, margin: '0 0 12px' }}>📩 Histórico de convites recebidos</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {recebidosRespondidos.map((c) => (
              <div key={c.id} style={cardStyle(COR_BORDA_STATUS[c.status])}>
                <span style={{ fontSize: 22 }}>{c.status === 'aceito' ? '✅' : '❌'}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 14, color: cores.texto }}>
                    {c.status === 'aceito'
                      ? <span>Você aceitou o convite e agora participa do projeto <strong>{c.projetoNome}</strong></span>
                      : <span>Você recusou o convite para o projeto <strong>{c.projetoNome}</strong></span>
                    }
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: 12, color: cores.textoSecundario }}>Convite de {c.deNome}</p>
                </div>
                <BadgeConviteStatus status={c.status} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* -------- Enviados aceitos (com ações) -------- */}
      {enviadosAceitos.length > 0 && (
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 15, color: cores.texto, margin: '0 0 12px' }}>👥 Membros ativos</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {enviadosAceitos.map((c) => {
              const permissao = permissaoAtual(c.projetoId, c.paraUid);
              const estaBloqueado = permissao === 'somente_leitura';
              return (
                <div key={c.id} style={cardStyle(cores.sucesso)}>
                  <span style={{ fontSize: 22 }}>✅</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 14, color: cores.texto }}>
                      <strong>{c.paraNome || c.paraEmail}</strong> participa do projeto <strong>{c.projetoNome}</strong>
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: cores.textoSecundario }}>{c.paraEmail}</p>
                    <div style={{ marginTop: 6 }}>
                      <BadgePermissao permissao={permissao} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <Btn
                      variante={estaBloqueado ? 'secundario' : 'fantasma'}
                      style={{
                        fontSize: 12,
                        padding: '6px 12px',
                        border: `1px solid ${estaBloqueado ? cores.sucesso : cores.erro}`,
                        color: estaBloqueado ? cores.sucesso : cores.erro,
                      }}
                      onClick={() => aoAlternarPermissao(c)}
                    >
                      {estaBloqueado ? '🔓 Reativar acesso' : '🔒 Bloquear acesso'}
                    </Btn>
                    <Btn
                      variante="perigo"
                      style={{ fontSize: 12, padding: '6px 12px' }}
                      onClick={() => aoRemoverMembro(c)}
                    >
                      🗑 Remover do projeto
                    </Btn>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* -------- Enviados pendentes/recusados -------- */}
      {enviadosOutros.length > 0 && (
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 15, color: cores.texto, margin: '0 0 12px' }}>📤 Convites enviados</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {enviadosOutros.map((c) => (
              <div key={c.id} style={cardStyle(COR_BORDA_STATUS[c.status])}>
                <span style={{ fontSize: 22 }}>{c.status === 'recusado' ? '❌' : '⏳'}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 14, color: cores.texto }}>
                    {c.status === 'recusado'
                      ? <span><strong>{c.paraEmail}</strong> recusou o convite para o projeto <strong>{c.projetoNome}</strong></span>
                      : <span>Convite enviado para <strong>{c.paraEmail}</strong> — projeto <strong>{c.projetoNome}</strong></span>
                    }
                  </p>
                  {c.status === 'pendente' && (
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: cores.textoSecundario }}>Aguardando resposta...</p>
                  )}
                </div>
                <BadgeConviteStatus status={c.status} />
              </div>
            ))}
          </div>
        </section>
      )}

      {convitesRecebidos.length === 0 && convitesEnviados.length === 0 && (
        <EstadoVazio
          titulo="Nenhum convite ainda"
          descricao="Envie convites para colaboradores participarem dos seus projetos."
          acao={projetosDono.length > 0 ? <Btn onClick={() => setModalAberto(true)}>+ Enviar convite</Btn> : null}
        />
      )}

      {/* -------- Modal enviar convite -------- */}
      <Modal titulo="Enviar convite" aberto={modalAberto} aoFechar={() => setModalAberto(false)}>
        <form onSubmit={aoEnviar}>
          {erroEnvio && (
            <div style={{ backgroundColor: '#FBE6E6', color: cores.erro, padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
              {erroEnvio}
            </div>
          )}
          <Campo label="E-mail do convidado*">
            <Input type="email" required value={form.paraEmail} onChange={(e) => setForm({ ...form, paraEmail: e.target.value })} placeholder="email@exemplo.com" />
          </Campo>
          <Campo label="Projeto*">
            <Select required value={form.projetoId} onChange={(e) => setForm({ ...form, projetoId: e.target.value })}>
              <option value="">Selecione um projeto...</option>
              {projetosDono.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </Select>
          </Campo>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Btn type="button" variante="secundario" onClick={() => setModalAberto(false)}>Cancelar</Btn>
            <Btn type="submit" disabled={enviando}>{enviando ? 'Enviando...' : 'Enviar convite'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function cardStyle(corBorda) {
  return {
    backgroundColor: '#fff',
    border: `1px solid ${cores.card}`,
    borderLeft: `4px solid ${corBorda}`,
    borderRadius: 10,
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    flexWrap: 'wrap',
  };
}
